import React, { useLayoutEffect, useRef, useMemo } from "react";
import images from "../../../images";
import { cn, formatDateToFakeMicroUTC } from "../../../Utils/helper";
import { usePostSaveQuestMutation } from "../../../services/apiSlice";
import StatusIcon from "../Status/StatusIcon/StatusIcon";

const CARD_STATUS = {
  COMPLETED: "COMPLETED",
  DISABLED: "DISABLED",
  ACTIVE: "ACTIVE",
};

const CardQuestPerType = ({
  slotAvailable,
  consumerStatus,
  questType,
  rallyCoin,
  expireTime,
  onClick,
  isIntroCompleted = false,
  participantId,
  onQuestSaved,
  questId,
  isParticipantsCompleted,
  isEnable,
  quest,
  isClickDisabled = false,
  showConnector = true,
  reward_stickers,
}) => {
  const now = new Date();
  const nowUTCISOString = formatDateToFakeMicroUTC(now);
  const expiredDate = expireTime ? new Date(expireTime.slice(0, 23)) : null;
  const nowDate = new Date(nowUTCISOString.slice(0, 23));
  const diffMs = expiredDate ? expiredDate.getTime() - nowDate.getTime() : 0;
  const diffSeconds = Math.round(diffMs / 1000);
  const initialRemainingTime = diffSeconds > 0 ? diffSeconds : 0;
  const [postSaveQuest] = usePostSaveQuestMutation();
  const hasProcessedExpiredRef = useRef(false);

  useLayoutEffect(() => {
    if (!initialRemainingTime || initialRemainingTime <= 0) {
      if (
        consumerStatus === "PAUSED_QUEST" &&
        participantId &&
        !hasProcessedExpiredRef.current
      ) {
        hasProcessedExpiredRef.current = true;
        const handleQuestEnd = async () => {
          try {
            await postSaveQuest({ participant_id: participantId }).unwrap();
          } catch (error) {
            console.error(`❌ Error saving quest ${questId}:`, error);
            hasProcessedExpiredRef.current = false;
          }
        };
        handleQuestEnd();
      }
      return;
    }

    hasProcessedExpiredRef.current = false;
  }, [
    initialRemainingTime,
    consumerStatus,
    participantId,
    postSaveQuest,
    questId,
    onQuestSaved,
    expireTime,
  ]);

  const isUserParticipating = useMemo(() => {
    return consumerStatus === "PAUSED_QUEST" || consumerStatus === "SAVE_QUEST";
  }, [consumerStatus]);

  const currentStatus = useMemo(() => {
    const statusBiz = quest?.quest_business?.status_biz;

    if (
      questType === "REFERRAL" &&
      (consumerStatus === "REVIEWING" ||
        consumerStatus === "SAVE_QUEST" ||
        consumerStatus === "PAUSED_QUEST" ||
        consumerStatus === "DOING")
    ) {
      return CARD_STATUS.ACTIVE;
    }

    if (consumerStatus === "COMPLETED" || consumerStatus === "REVIEWING") {
      return CARD_STATUS.COMPLETED;
    }
    if (consumerStatus === "DOING") {
      return CARD_STATUS.ACTIVE;
    }

    if (consumerStatus === "REJECT") {
      return CARD_STATUS.ACTIVE;
    }

    const isSlotFullAndNotParticipating =
      slotAvailable <= 0 && !isUserParticipating;

    if (
      statusBiz === "EXPIRED" ||
      isSlotFullAndNotParticipating ||
      isEnable === false
    ) {
      return CARD_STATUS.DISABLED;
    }

    const isActiveBizStatus =
      statusBiz === "IN_PROGRESS" || statusBiz === "PUBLISHED";
    const isActiveConsumerStatus =
      consumerStatus === "NOT_STARTED" ||
      !consumerStatus ||
      isUserParticipating;

    if (isActiveBizStatus && isActiveConsumerStatus) {
      return CARD_STATUS.ACTIVE;
    }
    // if (checkIsFuture(quest?.quest_business?.start_time)) {
    //   return CARD_STATUS.DISABLED;
    // }

    return CARD_STATUS.DISABLED;
  }, [
    consumerStatus,
    quest,
    slotAvailable,
    isEnable,
    isUserParticipating,
    questType,
  ]);

  const iconName = useMemo(() => {
    if (consumerStatus === "PAUSED_QUEST" || consumerStatus === "DOING")
      return "pause";
    if (consumerStatus === "SAVE_QUEST") return "bookmark";

    if (currentStatus === CARD_STATUS.COMPLETED) return "check";

    if (slotAvailable <= 0 && !isUserParticipating) return "full";

    if (currentStatus === CARD_STATUS.DISABLED) return "lock";

    if (currentStatus === CARD_STATUS.ACTIVE) {
      if (quest?.is_receipt_required) {
        return "ticket";
      } else {
        return "ticket";
      }
    }

    return "lock";
  }, [
    currentStatus,
    slotAvailable,
    consumerStatus,
    quest,
    isUserParticipating,
  ]);

  const isBanned = useMemo(() => {
    if (
      currentStatus === CARD_STATUS.ACTIVE &&
      !quest?.is_receipt_required &&
      (consumerStatus === "NOT_STARTED" || consumerStatus === "REJECT")
    ) {
      return true;
    }
    return false;
  }, [currentStatus, quest, consumerStatus]);

  const isCardDisabled = currentStatus === CARD_STATUS.DISABLED;
  const isCardCompleted = currentStatus === CARD_STATUS.COMPLETED;
  const isCardActive = currentStatus === CARD_STATUS.ACTIVE;

  const handleClick = () => {
    if (isClickDisabled) {
      return;
    }
    onClick();
  };

  return (
    <div
      className={cn(
        showConnector && "quest-line-item",
        // showConnector &&
        //   (isCardDisabled
        //     ? "quest-line-item--disabled"
        //     : "quest-line-item--active")
      )}
    >
      <div
        onClick={handleClick}
        className={cn(
          "flex justify-between rounded-md min-h-[129px]",
          isCardActive && "bg-[#ECEEFF] cursor-pointer",
          isCardCompleted && "bg-[#F4F2F2]",
          isCardDisabled && "bg-[#F4F2F2]",
          // consumerStatus === "PAUSED_QUEST" && "rounded-b-none border-b-0"
        )}
      >
        <div className="flex flex-row justify-between w-full">
          <div className="flex flex-col w-[130px] min-h-[138px] p-3 gap-3 justify-between">
            <div className="flex flex-col gap-[3px]">
              <h4
                className={cn(
                  "font-[Figtree] text-sm leading-[17px] font-bold not-italic line-clamp-1",
                  (isCardDisabled || isCardCompleted) && "text-[#A0A0A0]",
                  isCardActive && "text-[#253FF6]",
                )}
              >
                {quest?.quest_business?.name}
              </h4>

              <StatusIcon
                icon={iconName}
                isBanned={isBanned}
                classNameIcon={iconName === "check" ? "text-[10px]" : ""}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              {rallyCoin > 0 && (
                <div
                  className={cn(
                    "flex justify-center flex-row gap-[5px] items-center rounded-md px-[12px]",
                    isCardDisabled && "bg-[#A0A0A0]",
                    isCardActive && "bg-[#253FF6]",
                    isCardCompleted && "bg-[#E9E6E4]",
                  )}
                >
                  <span
                    className={cn("icon w-4 h-4 icon-rally-coin text-[#FFF]")}
                  />
                  <h6 className="text-white font-[Figtree] text-[18px] font-bold not-italic leading-[28px]">
                    {rallyCoin}
                  </h6>
                </div>
              )}
              {reward_stickers?.length > 0 && (
                <div
                  className={cn(
                    "flex justify-center flex-row gap-2 items-center rounded-md px-[12px]",
                    isCardDisabled && "bg-[#A0A0A0]",
                    isCardActive && "bg-[#253FF6]",
                    isCardCompleted && "bg-[#E9E6E4]",
                  )}
                >
                  <img
                    src={images.RallyStickerWhite}
                    className="w-4 h-4"
                    alt=""
                  />
                  <h6 className="text-white font-[Figtree] text-[18px] font-bold not-italic leading-[28px]">
                    {reward_stickers.length}
                  </h6>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* {consumerStatus === "PAUSED_QUEST" ? (
        <Timer className={"mt-0"} time={remainingTime} isStatic={true} />
      ) : (
        <></>
      )} */}
    </div>
  );
};

export default CardQuestPerType;
